/**
 * Categories section: bar chart (Top Categories) + category table from by-category.
 * One ComposableReportWidget instance; optional drill into products via callbacks.
 */
import { Box } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    ComposableReportWidget,
    type ReportWidgetColumnConfig,
    type ReportWidgetConfig,
    type ReportWidgetInteractionPayload,
    type ReportWidgetItem,
    type ReportWidgetTooltipRowConfig,
    type ReportWidgetTooltipRowsBuilder,
} from 'widgets/ComposableReportWidget';
import {
    buildSavingsRow,
    getTooltipPointFromPayload,
} from 'widgets/ComposableReportWidget/model/lib/tooltipHelpers';

import type { CategoryBreakdownDto } from 'entities/ReportSavingsSummary';

const TOP_CATEGORIES_COUNT = 10;

type SavingsSummaryCategoriesTableColumnId =
    | 'category'
    | 'totalSpend'
    | 'savings'
    | 'orders'
    | 'items'
    | 'missedSavings'
    | 'savingsPercentage'
    | 'avgSupplierSpend';

function categoryToWidgetItem(
    dto: CategoryBreakdownDto,
    index: number,
    uncategorizedLabel: string,
): ReportWidgetItem {
    const id = dto.categoryId ?? `category-${index}`;
    const label = dto.categoryTitle?.trim() || uncategorizedLabel;
    const savingsNum = Number.parseFloat(dto.savingsAmount);
    return {
        id,
        label,
        primaryValue: Number.isNaN(savingsNum) ? 0 : savingsNum,
        secondaryValues: {
            label,
            totalSpend: dto.totalSpend,
            savingsAmount: dto.savingsAmount,
            orderCount: dto.orderCount,
            itemCount: dto.itemCount,
            missedSavingsAmount: dto.missedSavingsAmount,
            savingsPercentage: dto.savingsPercentage,
            avgSupplierSpend: dto.avgSupplierSpend,
        },
        meta: { categoryId: dto.categoryId, categoryTitle: dto.categoryTitle },
    };
}

interface SavingsSummaryCategoriesSectionProps {
    byCategory: CategoryBreakdownDto[];
    loading?: boolean;
    onCategorySelect?: (payload: ReportWidgetInteractionPayload) => void;
    disableChartAnimation?: boolean;
    tableOnly?: boolean;
    subheader?: string;
    tableColumnIds?: SavingsSummaryCategoriesTableColumnId[];
    showBottomBorder?: boolean;
}

export const SavingsSummaryCategoriesSection = memo(
    ({
        byCategory,
        loading = false,
        onCategorySelect,
        disableChartAnimation = false,
        tableOnly = false,
        subheader,
        tableColumnIds,
        showBottomBorder = true,
    }: SavingsSummaryCategoriesSectionProps) => {
        const { t } = useTranslation();

        const items: ReportWidgetItem[] = useMemo(() => {
            const uncategorizedLabel = t('savingsSummary.uncategorized');

            return [...byCategory]
                .filter((category) => {
                    const savings =
                        Number.parseFloat(category.savingsAmount) || 0;
                    return savings !== 0;
                })
                .sort((a, b) => {
                    const aSavings = Number.parseFloat(a.savingsAmount) || 0;
                    const bSavings = Number.parseFloat(b.savingsAmount) || 0;
                    return bSavings - aSavings;
                })
                .slice(0, TOP_CATEGORIES_COUNT)
                .map((dto, i) =>
                    categoryToWidgetItem(dto, i, uncategorizedLabel),
                );
        }, [byCategory, t]);

        const handleInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                onCategorySelect?.(payload);
            },
            [onCategorySelect],
        );

        const tableColumns = useMemo<ReportWidgetColumnConfig[]>(() => {
            const allColumnsById: Record<string, ReportWidgetColumnConfig> = {
                category: {
                    id: 'category',
                    label: t('Category'),
                    valueKey: 'label',
                    format: 'text',
                    bold: true,
                },
                totalSpend: {
                    id: 'totalSpend',
                    label: t('savingsSummary.competitiveSpend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    headerTooltip: t('savingsSummary.competitiveSpendTooltip'),
                },
                savings: {
                    id: 'savings',
                    label: t('Total Savings'),
                    valueKey: 'savingsAmount',
                    format: 'currency',
                    highlightColor: 'metrics.positive',
                },
                orders: {
                    id: 'orders',
                    label: t('Orders'),
                    valueKey: 'orderCount',
                    format: 'number',
                },
                items: {
                    id: 'items',
                    label: t('Items'),
                    valueKey: 'itemCount',
                    format: 'number',
                },
                missedSavings: {
                    id: 'missedSavings',
                    label: t('savingsSummary.missedSavings'),
                    valueKey: 'missedSavingsAmount',
                    format: 'currency',
                    highlightColor: 'metrics.warning',
                },
                savingsPercentage: {
                    id: 'savingsPercentage',
                    label: t('savingsSummary.productsColumnPercentSaved'),
                    valueKey: 'savingsPercentage',
                    format: 'percent',
                    align: 'right',
                    highlightColor: 'metrics.positive',
                },
                avgSupplierSpend: {
                    id: 'avgSupplierSpend',
                    label: t('savingsSummary.avgSupplierSpend'),
                    valueKey: 'avgSupplierSpend',
                    format: 'currency',
                    align: 'right',
                },
            };

            const defaultOrder: SavingsSummaryCategoriesTableColumnId[] = [
                'category',
                'totalSpend',
                'savings',
                'orders',
                'items',
                'missedSavings',
            ];

            const orderedIds = tableColumnIds?.length
                ? tableColumnIds
                : defaultOrder;

            return orderedIds
                .map((id) => allColumnsById[id])
                .filter((column): column is ReportWidgetColumnConfig =>
                    Boolean(column),
                );
        }, [t, tableColumnIds]);

        const config: ReportWidgetConfig = useMemo(() => {
            const tooltipRowsBuilder: ReportWidgetTooltipRowsBuilder = (
                args,
            ) => {
                const resolved = getTooltipPointFromPayload(args);
                const item = resolved?.item;

                if (!item) {
                    return null;
                }

                const savingsNum =
                    typeof item.primaryValue === 'number'
                        ? item.primaryValue
                        : Number.parseFloat(String(item.primaryValue)) || 0;

                const tooltipRows: ReportWidgetTooltipRowConfig[] = [
                    {
                        id: 'header',
                        variant: 'header',
                        label: item.label,
                        boldLabel: true,
                        mutedLabel: true,
                        marginBottom: 4,
                    },
                    buildSavingsRow({
                        id: 'savings',
                        label: t('Savings'),
                        amount: savingsNum,
                        total: args.total,
                        valueFormatter: args.valueFormatter,
                    }),
                ];

                return tooltipRows;
            };

            return {
                widgetId: 'savings-summary-categories',
                title: t('savingsSummary.categoriesSectionTitle'),
                description: subheader,
                visualization: {
                    enabled: !tableOnly,
                    type: 'bar',
                    options: {
                        colors: ['#4BA054'],
                        showLegend: false,
                        valueFormatter: (v) =>
                            new Intl.NumberFormat(undefined, {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(
                                typeof v === 'number'
                                    ? v
                                    : Number.parseFloat(String(v)) || 0,
                            ),
                        yAxisTickFormatter: (value) => {
                            if (typeof value !== 'number') {
                                return String(value);
                            }

                            if (value === 0) {
                                return '0';
                            }

                            const rounded = Math.round(value);
                            return new Intl.NumberFormat(undefined, {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                            }).format(rounded);
                        },
                        tooltipRowsBuilder,
                    },
                },
                table: {
                    enabled: tableOnly,
                    columns: tableColumns,
                },
                onRowClick: onCategorySelect ? handleInteraction : undefined,
                onChartItemClick:
                    !tableOnly && onCategorySelect
                        ? handleInteraction
                        : undefined,
            };
        }, [
            t,
            onCategorySelect,
            handleInteraction,
            tableOnly,
            subheader,
            tableColumns,
        ]);

        return (
            <Box
                data-testclass="savings-summary-categories-section"
                data-pdf-section="true"
                sx={{
                    borderBottom: showBottomBorder
                        ? '1px solid #E0E0E0'
                        : 'none',
                    paddingBottom: showBottomBorder ? '40px' : 0,
                }}
            >
                <ComposableReportWidget
                    config={config}
                    items={items}
                    loading={loading}
                    isPrinting={disableChartAnimation}
                    disableChartAnimation={disableChartAnimation}
                />
            </Box>
        );
    },
);

SavingsSummaryCategoriesSection.displayName = 'SavingsSummaryCategoriesSection';
